/**
 * This device's terminal registration (08-clients.md "Staff app"): the ids it got from
 * `POST {o}/terminals/register` and the random secret it generated. In the browser it
 * lives in localStorage; the Tauri and Capacitor shells will keep the secret in their
 * secure storage instead (M4). Guarded: without storage the device is simply not a
 * registered terminal.
 */
import { TERMINAL_ROLES, type TerminalRole } from '@qrkey/contracts';
import { useSyncExternalStore } from 'react';

export interface DeviceTerminal {
  companyId: string;
  outletId: string;
  terminalId: string;
  secret: string;
  role: TerminalRole;
  name: string;
}

const KEY = 'qrkey.staff.terminal.v1';
const listeners = new Set<() => void>();

function read(): DeviceTerminal | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as Partial<DeviceTerminal>;
    return typeof t.terminalId === 'string' &&
      typeof t.secret === 'string' &&
      typeof t.companyId === 'string' &&
      typeof t.outletId === 'string' &&
      TERMINAL_ROLES.includes(t.role as TerminalRole)
      ? (t as DeviceTerminal)
      : null;
  } catch {
    return null;
  }
}

let current: DeviceTerminal | null = typeof window === 'undefined' ? null : read();

export const terminalStore = {
  get: (): DeviceTerminal | null => current,
  set(terminal: DeviceTerminal | null) {
    current = terminal;
    try {
      if (terminal) window.localStorage.setItem(KEY, JSON.stringify(terminal));
      else window.localStorage.removeItem(KEY);
    } catch {
      // Not persisted; this tab still knows it.
    }
    listeners.forEach((l) => l());
  },
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function useTerminal(): DeviceTerminal | null {
  return useSyncExternalStore(terminalStore.subscribe, terminalStore.get, terminalStore.get);
}

/** The credential the API wants with sign-in and PIN requests. */
export function terminalCredential(t: DeviceTerminal) {
  return {
    companyId: t.companyId,
    outletId: t.outletId,
    terminalId: t.terminalId,
    secret: t.secret,
  };
}

/** 32 random bytes, base64url (deviceSecretSchema: 43+ chars). Generated once per registration. */
export function newDeviceSecret(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Till roles get a PIN screen; a back-office terminal signs in with a password. */
export function isTillRole(role: TerminalRole): boolean {
  return role !== 'backoffice';
}
